import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import HrDashboardLayout from '../components/layouts/HrDashboardLayout';
import Button from '../components/atoms/Button';
import Badge from '../components/atoms/Badge';
import Icon from '../components/atoms/Icon';
import SessionManager from '../utils/sessionManager';
import { getApplications, updateApplicationStatus } from '../services/api';

const HrInterviewDashboard = () => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, under_review, offer, rejected

  // Get HR company name from session
  const getHrCompanyName = () => {
    SessionManager.migrateLegacySession();
    const hrSession = SessionManager.getSession('hr');
    return hrSession?.company || hrSession?.profile?.company_name || 'Company';
  };

  const companyName = getHrCompanyName();

  // Check HR authentication
  useEffect(() => {
    const isLoggedInAsHr = SessionManager.isLoggedIn('hr');
    const currentSession = SessionManager.getCurrentSession();
    
    if (!isLoggedInAsHr || (currentSession && currentSession.role !== 'hr')) {
      navigate('/hr_login');
      return;
    }
    
    loadApplications();
  }, [navigate]);

  const loadApplications = async () => {
    try {
      setLoading(true);
      const data = await getApplications({ company: companyName });
      
      // Filter applications with interviews (completed or in progress)
      const withInterviews = data.filter(app => {
        // Include if has interview_id OR status indicates interview activity
        return app.interview_details?.interview_id || 
               app.status === 'interview_completed' || 
               app.status === 'under_review' ||
               app.status === 'interview' ||
               app.status === 'offer' ||
               app.status === 'rejected';
      });
      
      setApplications(withInterviews);
      console.log(`Loaded ${withInterviews.length} applications with interviews`);
    } catch (err) {
      console.error('Error loading applications:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'applied': { variant: 'info', label: 'Applied' },
      'under_review': { variant: 'warning', label: 'Under Review' },
      'interview_scheduled': { variant: 'info', label: 'Interview Scheduled' },
      'interview_completed': { variant: 'warning', label: 'Under Review' },
      'offer': { variant: 'success', label: 'Offer' },
      'rejected': { variant: 'error', label: 'Rejected' },
    };
    
    const config = statusMap[status] || { variant: 'default', label: status };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const updateStatus = async (applicationId, newStatus) => {
    try {
      await updateApplicationStatus(applicationId, newStatus);
      loadApplications();
    } catch (err) {
      console.error('Error updating status:', err);
    }
  };

  const filteredApplications = applications.filter(app => {
    if (filter === 'all') return true;
    if (filter === 'under_review') return app.status === 'interview_completed' || app.status === 'under_review';
    if (filter === 'offer') return app.status === 'offer';
    if (filter === 'rejected') return app.status === 'rejected';
    return true;
  });

  if (loading) {
    return (
      <HrDashboardLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </HrDashboardLayout>
    );
  }

  return (
    <HrDashboardLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Interview Assessments</h1>
          <p className="text-gray-600 mt-1">Review candidate interviews and make hiring decisions</p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All ({applications.length})
            </button>
            <button
              onClick={() => setFilter('under_review')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === 'under_review' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Under Review ({applications.filter(a => a.status === 'interview_completed' || a.status === 'under_review').length})
            </button>
            <button
              onClick={() => setFilter('offer')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === 'offer' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Offer ({applications.filter(a => a.status === 'offer').length})
            </button>
            <button
              onClick={() => setFilter('rejected')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === 'rejected' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Rejected ({applications.filter(a => a.status === 'rejected').length})
            </button>
          </div>
        </div>

        {/* Applications Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                    Candidate
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                    Job
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                    ATS
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                    Assessment
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                    Stage
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                    Applied
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredApplications.map((app) => (
                  <tr key={app.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <div className="font-medium text-gray-900">
                          {app.candidate_name}
                        </div>
                        <div className="text-sm text-gray-600">
                          {app.candidate_email}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {app.job_title || app.job_id}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`font-semibold ${
                        app.ats_score >= 70 ? 'text-green-600' :
                        app.ats_score >= 50 ? 'text-yellow-600' :
                        'text-red-600'
                      }`}>
                        {app.ats_score ? `${app.ats_score.toFixed(1)}%` : 'N/A'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {app.interview_details?.interview_score !== undefined && app.interview_details?.interview_score !== null ? (
                        <div className="flex items-center gap-2">
                          <span className={`font-bold text-lg ${
                            app.interview_details.interview_score >= 70 ? 'text-green-600' :
                            app.interview_details.interview_score >= 50 ? 'text-yellow-600' :
                            'text-red-600'
                          }`}>
                            {Math.round(app.interview_details.interview_score)}%
                          </span>
                          {app.interview_details.interview_score >= 70 && (
                            <Icon name="check_circle" size={16} className="text-green-600" />
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-500 text-sm italic">
                          Pending
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(app.status)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-600">
                        {new Date(app.applied_at).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2 flex-wrap">
                        {/* View Report / Review button */}
                        {(app.interview_details?.interview_id || app.interview_id) ? (
                          <Button
                            size="sm"
                            variant={app.interview_details?.interview_score != null ? 'outline' : 'primary'}
                            onClick={() => navigate(`/hr/interview-review/${app.interview_details?.interview_id || app.interview_id}`)}
                          >
                            <Icon name="assessment" size={16} className="mr-1" />
                            {app.interview_details?.interview_score != null ? 'View' : 'Review'}
                          </Button>
                        ) : (
                          <span className="text-xs text-gray-400 italic">No interview</span>
                        )}
                        
                        {/* Accept button */}
                        {app.status !== 'offer' && (
                          <Button
                            size="sm"
                            variant="success"
                            onClick={() => updateStatus(app.id, 'offer')}
                          >
                            <Icon name="check_circle" size={16} className="mr-1" />
                            Accept
                          </Button>
                        )}
                        
                        {/* Reject button */}
                        {app.status !== 'rejected' && app.status !== 'offer' && (
                          <Button
                            size="sm"
                            variant="error"
                            onClick={() => updateStatus(app.id, 'rejected')}
                          >
                            <Icon name="cancel" size={16} className="mr-1" />
                            Reject
                          </Button>
                        )}
                        
                        {/* Offer Sent badge */}
                        {app.status === 'offer' && (
                          <span className="px-3 py-1 text-sm font-medium rounded-lg bg-green-100 text-green-700">
                            Offer Sent
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredApplications.length === 0 && (
            <div className="text-center py-12">
              <Icon name="inbox" size={48} className="text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">
                No applications found
              </p>
            </div>
          )}
        </div>
      </div>
    </HrDashboardLayout>
  );
};

export default HrInterviewDashboard;
